import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import StatelyForm from "./StatelyForm";

import { Button } from "@te-whatu-ora/anatomic";
import {
  OnboardingContext,
  OnboardingUserData,
} from "@context/onboardingContext";
import onboardingMachine from "@stateMachines/onboarding";
import { Analytics } from "@utils/analytics";

const Container = styled.div`
  padding-bottom: 40px;
`;

const TitleContainer = styled.div`
  margin-bottom: 74px;
  max-width: 414px;
`;

const Title = styled.h1`
  margin-bottom: 12px;
`;

const SubTitle = styled.div`
  color: #000;
  font-size: var(--font-size-xl);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 24px;
  letter-spacing: var(--letter-spacing-wider);
  max-width: 394px;
`;

const OnboardingForm = ({
  onFinishForm,
  isFormReset,
}: {
  onFinishForm: () => void;
  isFormReset: boolean;
}) => {
  const [isFormFinished, setIsFormFinished] = useState(false);
  const finalStepRef = useRef<HTMLDivElement>(null);

  const { updateOnboardingData } = useContext(OnboardingContext) || {};

  useEffect(() => {
    if (isFormReset) {
      setIsFormFinished(false);
    }
  }, [isFormReset]);

  const handleFormFinished = useCallback(
    (context: { link: boolean; userData: OnboardingUserData }) => {
      setIsFormFinished(!!context.link);
      updateOnboardingData?.(context.userData);
      
      if (context.link && context.userData) {
        Analytics.trackOnboardingComplete({
          user_role: context.userData.role,
          user_location: context.userData.location,
          user_district: context.userData.district,
          practice_area: context.userData.practiceArea,
          event_category: 'form_interaction'
        });
      }
    },
    [updateOnboardingData]
  );

  return (
    <Container ref={isFormFinished ? finalStepRef : null}>
      <TitleContainer>
        <Title>
          {isFormFinished
            ? "Thanks! You're all set up"
            : "Let's get you set up"}
        </Title>
        <SubTitle>
          {isFormFinished
            ? "Review antibiotics prescribing guidelines to learn about best practice for common use cases in Aotearoa (New Zealand)."
            : "This will only take 1 min and helps us to improve the guidelines."}
        </SubTitle>
      </TitleContainer>
      {isFormFinished ? (
        <Button onClick={() => {
          Analytics.trackButtonClick('review_guidelines', 'onboarding_complete', {
            destination: '/guidelines',
            button_text: 'Review guidelines'
          });
          onFinishForm();
        }}>Review guidelines</Button>
      ) : (
        <StatelyForm
          stateMachine={onboardingMachine}
          onFormFinished={handleFormFinished}
        />
      )}
    </Container>
  );
};

export default OnboardingForm;
